// src/components/Layout/Header.tsx

import { FC, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, Variants } from 'framer-motion';
import styles from '../../css/Header.module.css';
import { Briefcase, ChevronRight, Menu, X } from 'lucide-react';
import { useMediaQuery } from '../../hooks/useMediaQuery';

// Import Logo
import LogoImg from '../../assets/images/OTATECHlogo.jpeg';

const Header: FC = () => {
    const isMobile = useMediaQuery('(max-width: 968px)');
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
    const closeMenu = () => setIsMenuOpen(false);

    // Animation Variants (Explicitly Typed to fix TS errors)
    const menuVariants: Variants = {
        closed: { 
            opacity: 0, 
            height: 0, 
            transition: { duration: 0.3, ease: "easeInOut" }
        },
        open: { 
            opacity: 1, 
            height: "auto", 
            transition: { duration: 0.3, ease: "easeInOut" }
        }
    };

    const navItemVariants: Variants = {
        hidden: { opacity: 0, x: -20 },
        visible: { opacity: 1, x: 0 }
    };

    return (
        <header className={styles.header}>
            <div className={styles.container}>
                
                {/* 1. LOGO */}
                <Link to="/" className={styles.logoWrapper} onClick={closeMenu}>
                    <img src={LogoImg} alt="OTA TECH Inc." className={styles.logoImage} />
                </Link>

                {/* 2. DESKTOP NAVIGATION */}
                {!isMobile && (
                    <>
                        <nav className={styles.nav}>
                            {['Portfolio', 'Investors', 'Careers', 'Contact', 'About'].map((item) => (
                                <Link 
                                    key={item} 
                                    to={`/${item.toLowerCase()}`} 
                                    className={styles.navLink}
                                >
                                    {item}
                                </Link>
                            ))}
                        </nav>
                        <div className={styles.ctaGroup}>
                            {/* Enhanced ANNA AI Button */}
                            <motion.a 
                                href="https://www.anna-ai.com/careers" 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className={styles.annaLink}
                                whileHover={{ scale: 1.03, borderColor: "#a1a1aa" }}
                                whileTap={{ scale: 0.97 }}
                            >
                                <Briefcase size={14} strokeWidth={2} /> 
                                <span>Apply via ANNA AI</span>
                            </motion.a>
                            
                            <Link to="/investors" className={styles.investCta}>
                                Invest Now <ChevronRight size={16} />
                            </Link>
                        </div>
                    </>
                )}

                {/* 3. MOBILE TOGGLE */}
                {isMobile && (
                    <button 
                        className={styles.mobileToggle} 
                        onClick={toggleMenu}
                        aria-label="Toggle Menu"
                    >
                        <motion.div
                            initial={false}
                            animate={{ rotate: isMenuOpen ? 90 : 0 }}
                            transition={{ duration: 0.2 }}
                        >
                            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
                        </motion.div>
                    </button>
                )}

                {/* 4. MOBILE MENU DROPDOWN */}
                <AnimatePresence>
                    {isMobile && isMenuOpen && (
                        <motion.div 
                            className={styles.mobileMenu}
                            variants={menuVariants}
                            initial="closed"
                            animate="open"
                            exit="closed"
                        >
                            <nav className={styles.mobileNav}>
                                {['Portfolio', 'Investors', 'Careers', 'About', 'Contact'].map((item, index) => (
                                    <motion.div
                                        key={item}
                                        variants={navItemVariants}
                                        initial="hidden"
                                        animate="visible"
                                        transition={{ delay: index * 0.05 }}
                                    >
                                        <Link 
                                            to={`/${item.toLowerCase()}`} 
                                            className={styles.mobileNavLink} 
                                            onClick={closeMenu}
                                        >
                                            {item}
                                        </Link>
                                    </motion.div>
                                ))}
                            </nav>
                            
                            <motion.div 
                                className={styles.mobileCtaGroup}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.3 }}
                            >
                                <Link to="/investors" className={styles.investCta} onClick={closeMenu}>
                                    Invest Now <ChevronRight size={16} />
                                </Link>
                                <a href="#" className={styles.textLinkMobile}>
                                    <Briefcase size={16} /> Apply via ANNA AI
                                </a>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

            </div>
        </header>
    );
};

export default Header;